import { useState } from 'react';
import { DEMO_LICENSE_KEY } from '../lib/license';

interface UpgradeCardProps {
  isPro: boolean;
  licenseKey: string | null;
  licenseError: string | null;
  rowCount: number;
  rawRowCount: number;
  truncated: boolean;
  onActivate: (key: string) => void;
  onDeactivate: () => void;
}

export function UpgradeCard({
  isPro,
  licenseKey,
  licenseError,
  rowCount,
  rawRowCount,
  truncated,
  onActivate,
  onDeactivate,
}: UpgradeCardProps) {
  const [key, setKey] = useState('');

  return (
    <section id="upgrade" className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-panel sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="text-sm font-semibold text-slate-100">
            Freemium gate {isPro && <span className="text-lime-400">· Pro active</span>}
          </h2>
          <ul className="mt-2 space-y-1 text-xs text-slate-400">
            <Feature included>Up to 500 rows per file</Feature>
            <Feature included>Schema inference, anomaly grid &amp; all 4 code generators</Feature>
            <Feature included={isPro}>Unlimited rows (50k+ in a worker)</Feature>
            <Feature included={isPro}>Chaos-dataset exports (.csv / .json)</Feature>
            <Feature included={isPro}>Saved schema templates + type overrides</Feature>
          </ul>
          {truncated && (
            <p className="mt-2 text-xs text-amber-300">
              Showing {rowCount.toLocaleString()} of {rawRowCount.toLocaleString()} rows — Pro unlocks the full file.
            </p>
          )}
        </div>
        <div className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-950/60 p-4">
          {isPro ? (
            <div className="text-xs">
              <p className="font-mono text-lime-300">{licenseKey}</p>
              <button
                type="button"
                onClick={onDeactivate}
                className="mt-3 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-400 transition hover:border-rose-500/60 hover:text-rose-300"
              >
                Deactivate license
              </button>
            </div>
          ) : (
            <div>
              <label htmlFor="pro-key" className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Pro license key
              </label>
              <div className="mt-1.5 flex gap-2">
                <input
                  id="pro-key"
                  value={key}
                  onChange={(event) => setKey(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && onActivate(key)}
                  placeholder="CMCSV-XXXXX-XXXXX-XXXXX"
                  spellCheck={false}
                  className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 font-mono text-xs text-slate-100 outline-none placeholder:text-slate-700 focus:border-amber-500"
                />
                <button
                  type="button"
                  onClick={() => onActivate(key)}
                  className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-slate-950 transition hover:bg-amber-400"
                >
                  Activate
                </button>
              </div>
              {licenseError && <p className="mt-2 text-[11px] text-rose-300">{licenseError}</p>}
              <p className="mt-2 text-[11px] text-slate-600">
                No key? Try the offline demo key{' '}
                <button
                  type="button"
                  onClick={() => onActivate(DEMO_LICENSE_KEY)}
                  className="font-mono text-amber-400/90 underline"
                >
                  {DEMO_LICENSE_KEY}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Feature({ included, children }: { included: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <span className={included ? 'text-lime-400' : 'text-slate-600'}>{included ? '✓' : '🔒'}</span>
      <span className={included ? '' : 'text-slate-600'}>{children}</span>
    </li>
  );
}
